/**
 * Envelopes visuais compartilhados pelos relatórios.
 *
 * `BlurOverlay` é o padrão de upsell da página: usuário sem o plano vê o
 * conteúdo (ou uma amostra) borrado com o CTA em cima, nunca uma tela vazia.
 * `AwaitingData` é o vazio legítimo de quem TEM acesso e ainda não tem dado.
 */
import { AlertTriangle, Clock, Crown, FilterX, Lock } from 'lucide-react';
import { Button3D } from '@/components/ui/button-3d';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

interface AwaitingDataProps {
  /** Linha de ajuda ("Crie orçamentos para ver estatísticas"). */
  hint?: string;
}

export function AwaitingData({ hint = 'Crie orçamentos para ver estatísticas' }: AwaitingDataProps) {
  return (
    <div className="flex h-full flex-col items-center justify-center py-8">
      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted/50">
        <Clock className="h-6 w-6 text-muted-foreground" />
      </div>
      <p className="text-sm font-medium text-muted-foreground">Aguardando dados</p>
      <p className="mt-1 text-xs text-muted-foreground/70">{hint}</p>
    </div>
  );
}

export function FilteredEmptyState({
  activeCount,
  onClear,
}: {
  activeCount: number;
  onClear: () => void;
}) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-14 text-center">
        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted/50">
          <FilterX className="h-6 w-6 text-muted-foreground" />
        </div>
        <p className="text-sm font-medium">Nenhum resultado com estes filtros</p>
        <p className="mt-1 text-xs text-muted-foreground">
          {activeCount} {activeCount === 1 ? 'filtro ativo' : 'filtros ativos'}
        </p>
        <Button type="button" variant="outline" className="mt-4" onClick={onClear}>
          Limpar filtros
        </Button>
      </CardContent>
    </Card>
  );
}

interface BlurOverlayProps {
  children: React.ReactNode;
  /** Quando falso, borra o conteúdo e sobrepõe o CTA de desbloqueio. */
  allowed: boolean;
  /** Abre o paywall — normalmente `feature.require()`. */
  onUnlock: () => void;
}

/**
 * Aviso âmbar de que o número NÃO está completo — leitura truncada, item sem
 * custo, linha descartada. Existe como peça compartilhada porque "no silent
 * caps" vale para todos os relatórios: sempre que uma agregação deixa algo de
 * fora, a tela diz o quê e quanto.
 */
export function WarningCard({ children }: { children: React.ReactNode }) {
  return (
    <Card className="border-amber-300/60 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-900/10">
      <CardContent className="flex items-start gap-2 p-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <p className="text-xs leading-snug">{children}</p>
      </CardContent>
    </Card>
  );
}

interface ListTailProps {
  total: number;
  shown: number;
  /** Substantivo no SINGULAR ("item", "cliente"); o plural sai com "s". */
  noun: string;
}

/**
 * Rodapé de lista cortada. No modo Resumo diz quantos ficaram de fora e como
 * ver; no Detalhado só aparece se o teto de segurança foi atingido. Nunca um
 * corte mudo.
 */
export function ListTail({ total, shown, noun }: ListTailProps) {
  if (total <= shown) return null;
  const rest = total - shown;
  return (
    <p className="pt-1 text-xs text-muted-foreground">
      + {rest} {rest === 1 ? `outro ${noun}` : `outros ${noun}s`} — veja em{' '}
      <span className="font-medium">Detalhado</span> ou na exportação.
    </p>
  );
}

export function BlurOverlay({ children, allowed, onUnlock }: BlurOverlayProps) {
  if (allowed) return <>{children}</>;

  return (
    <div className="relative">
      <div className="pointer-events-none select-none opacity-60 blur-[6px]">{children}</div>
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/30">
        <Lock className="mb-2 h-8 w-8 text-muted-foreground" />
        <p className="text-sm font-medium text-muted-foreground">Disponível no PRO</p>
        <Button3D color="amber" size="sm" className="mt-3" onClick={onUnlock}>
          <Crown className="mr-2 h-4 w-4" />
          Desbloquear
        </Button3D>
      </div>
    </div>
  );
}
